import { Component, OnInit, Input, Output, EventEmitter, Inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { Observable } from 'rxjs';

import { Area } from '../../../models/area.interface';
import { QuoteArea } from '../../../models/quote.area.interface';
import { ItemCoverageType, ItemType } from '../../../models/item.type.interface';
import { RValue } from '../../../models/r.value.interface';
import { ItemUsage } from '../../../models/item.usage.interface';
import { ItemArea } from '../../../models/item.area.interface';
import { QuoteAreaAccessory } from '../../../models/quote.area.accessory.interface';
import { QuoteService } from '../../../../quote/services/quote.service';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AreaDialogData } from '../quote-details/quote-details.component';
import { QuoteAreaItem } from '../../../models/quote.area.item.interface';
import { AbstractBiditComponent } from '../../abstract.bidit.component';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ItemMaster } from '../../../models/item.master.interface';
import { NgForm } from '@angular/forms';
import { QuoteItemNoteTemplate } from '../../../models/quote.item.note.template.interface';

@Component({
  selector: 'app-quote-area',
  templateUrl: './quote-area.dialog.html',
  styleUrls: ['./quote-area.dialog.scss']
})
export class QuoteAreaDialog extends AbstractBiditComponent implements OnInit {
  private areaObservable$ = new Observable<Area>();
  private areaSubscription;

  productFilterItemType_ID: number;
  productFilterRValue: number;

  coverageEnum = ItemCoverageType;
  productUsage: boolean = false;
  productAeroBarrier: boolean = false;
  isAreaOptionGroup: boolean = false;
  showTemplates: boolean = false;
  selectedTemplate: number = null;
  
  quote_ID: number;
  quoteArea: QuoteArea = {
  } as QuoteArea;
  quoteAreaItem: QuoteAreaItem = {
    accessories: new Array()
  } as QuoteAreaItem;

  areas: Area[];
  quoteItemNoteTemplates: QuoteItemNoteTemplate[] = [];
  accessories: QuoteAreaAccessory[];
  itemTypes: ItemType[];
  productRValues: RValue[];
  productItems: ItemMaster[];
  productUsages: ItemUsage[];
  quotaAreaGroups: number[];
  constructor(private quoteService: QuoteService, private router: Router, public dialogRef: MatDialogRef<QuoteAreaDialog>,
    @Inject(MAT_DIALOG_DATA) public data: AreaDialogData, deviceDetectorService: DeviceDetectorService) {
    super(deviceDetectorService);
  }


  @Output() updated = new EventEmitter<QuoteAreaItem>();
  @Output() canceled = new EventEmitter<QuoteAreaItem>();

  ngOnInit() {
    // wait until quote loaded before loading further info
    this.areaSubscription = this.areaObservable$.subscribe(area => {
      if (area == null) {
        this.quoteArea.area = {} as Area;
        this.quoteAreaItem.area_ID = null;
      } else {
        this.quoteArea.area = area;
        this.quoteAreaItem.area_ID = area.area_ID;

        this.mergeAccessories(area.itemAreas);

        this.refreshProductItems();
      }
    });

    this.selectedTemplate = null;
    this.showTemplates = false;
    this.areas = this.data.areas;
    this.loadQuoteArea(this.data.quoteId, this.data.quoteAreaItemId, this.data.quoteArea);

    this.quoteService.getItemTypes()
      .subscribe({
        next: (data) => {
          this.itemTypes = data.filter(it => !it.itemType_IsAccessory && it.itemType_IsActive);
        }
      });      
  }

  loadQuoteArea(quoteId: number, quoteAreaItemId: number, quoteArea: QuoteArea) {
    this.quote_ID = quoteId;
    this.quoteAreaItem.area_IsMaterialOnly = false;

    if (quoteArea != null) {
      this.quoteArea = quoteArea;
      this.quoteAreaItem.quoteArea_ID = quoteArea.quoteArea_ID;
      this.quoteAreaItem.area_ID = quoteArea.area_ID;

      if (quoteAreaItemId == null && quoteArea.quoteItem != null) {
        // 2022 q1 - add alternate item, default sqr ftg
        this.quoteAreaItem.area_SqrFtg = quoteArea.quoteItem.area_SqrFtg;

        let area = this.areas.find(a => a.area_ID == this.quoteArea.area_ID);
        //area.subAreas?.sort((a, b) => a.subArea_Name > b.subArea_Name ? 1 : -1)
        this.areaSubscription.next(area);
      }
    }

    if (quoteAreaItemId == null) return;

    this.quoteService.getQuoteAreaItem(quoteAreaItemId)
      .subscribe({
        next: (data) => {
          this.quoteArea = data.quoteArea;
          this.quoteAreaItem = data.quoteAreaItem;

          let product = this.quoteAreaItem.item;
          if (product) {
            let productType = product.itemType;
            this.productFilterItemType_ID = product.itemType_ID;
            this.productFilterRValue = product.rValue_ID;

            this.productUsages = product.itemUsages;
            this.productUsage = productType.itemCoverageType_ID != null && productType.itemCoverageType_ID != this.coverageEnum.AeroBarrier;
            this.productAeroBarrier = productType.itemCoverageType_ID != null && productType.itemCoverageType_ID == this.coverageEnum.AeroBarrier;
            this.isAreaOptionGroup = this.quoteAreaItem.optionGroup !== null
          }

          // race condition with fetching areas
          if (this.areas != null) {
            let area = this.areas.find(a => a.area_ID == this.quoteArea.area_ID);
            //area.subAreas?.sort((a, b) => a.subArea_Name > b.subArea_Name ? 1 : -1)
            this.areaSubscription.next(area);
          }
        },
        error: (error: string) => this.onError(error)
      });
  }

  onAreaChange(areaId: Number) {
    let area = this.areas.find(c => c.area_ID == areaId);
    //area?.subAreas?.sort((a, b) => a.subArea_Name > b.subArea_Name ? 1 : -1)
    this.areaSubscription.next(area);
    this.isAreaOptionGroup = false;
  }


  quoteItemNoteTemplateChange(quoteItemTemplateID: number) {
    let itemNoteTemplate = this.data.quoteItemNoteTemplates.find(c => c.quoteItemNoteTemplate_ID == quoteItemTemplateID);
    this.quoteAreaItem.area_ItemCustomNotes = itemNoteTemplate?.quoteItemNoteTemplate_Desc;
  }

  refreshProductItemType() {
    this.productFilterRValue = null;
    let itemType = this.itemTypes.find(it => it.itemType_ID == this.productFilterItemType_ID);
    this.productUsage = (itemType != null && (itemType.itemCoverageType_ID != null && itemType.itemCoverageType_ID != this.coverageEnum.AeroBarrier));
    this.productAeroBarrier = (itemType != null && (itemType.itemCoverageType_ID != null && itemType.itemCoverageType_ID == this.coverageEnum.AeroBarrier));

    this.refreshProductItems();
  }

  refreshProductItems() {
    this.quoteService.getRValues(this.productFilterItemType_ID)
      .subscribe({
        next: (data) => {
          this.productRValues = data;
        },
        error: (error: string) => this.onError(error)
      });

    if (this.productFilterItemType_ID == null) {
      this.productItems = null;
      return;
    }

    this.quoteService.getItems(this.productFilterItemType_ID, this.productFilterRValue)
      .subscribe({
        next: (data) => {
          this.productItems = data;
        },
        error: (error: string) => this.onError(error)
      });
  };

  refreshProductUsages() {
    this.quoteService.getItemUsages(this.quoteAreaItem.item_ID)
      .subscribe({
        next: (data) => {
          this.productUsages = data;
        },
        error: (error: string) => this.onError(error)
      });
  };

  useTemplateChange(selected: boolean) {
    this.showTemplates = selected;
  }

  mergeAccessories(itemAreas: ItemArea[]) {
    this.quoteAreaItem.accessories = this.quoteAreaItem.accessories.filter(acc =>
      acc.accessory_Quantity != null && acc.accessory_Quantity != 0);

    for (let itemArea of itemAreas) {
      //if (!itemArea.item.itemType?.itemType_IsAccessory) continue;

      let item = this.quoteAreaItem.accessories.find((acc) => {
        return acc.item_ID == itemArea.item_ID
      });

      if (item == null) {
        let quoteItem = {
          item: itemArea.item,
          item_ID: itemArea.item_ID,
          accessory_Cost: itemArea.item.item_Cost
        } as QuoteAreaAccessory;
        this.quoteAreaItem.accessories.push(quoteItem);
      }
    }
  }

  removeCustomAccessory(accessory: QuoteAreaAccessory) {
    let index = this.quoteAreaItem.accessories.indexOf(accessory);
    this.quoteAreaItem.accessories.splice(index, 1);
  }

  addCustomAccessory() {
    let accessory = {} as QuoteAreaAccessory;
    this.quoteAreaItem.accessories.push(accessory);
  }

  get showAdditionalLabor(): boolean {
    return !this.quoteAreaItem.area_IsMaterialOnly;
  }

  saveArea(form: NgForm) {
    this.errors = '';

    if (form.valid) {
      this.isRequesting = true;

      if (this.quoteAreaItem.area_IsCustomItem) {
        this.quoteAreaItem.area_ID = -1
      }

      this.quoteService.saveQuoteAreaItem(this.quote_ID, this.quoteAreaItem)
        .pipe(finalize(() => this.isRequesting = false))
        .subscribe(
          {next:(result) => {
            if (result) {
              this.alertComplete(true);
              form.resetForm();
              this.dialogRef.close(result);
            }
          },
          error: (error: string) => this.onError(error)});
    }
  }

  cancelEdit() {
    var proceed = confirm($localize`Are you sure?`);
    if (!proceed) return;
        
    this.alertComplete(false);
    this.dialogRef.close();
  }

  alertComplete(success: boolean) {
    if (success)
      this.updated.emit(this.quoteAreaItem);
    else
      this.canceled.emit(this.quoteAreaItem);

    this.quoteAreaItem = {
      accessories: new Array()
    } as QuoteAreaItem;

    this.productFilterItemType_ID = null;
    this.productFilterRValue = null;
  }

  deleteArea(form: NgForm) {
    this.errors = '';
    this.isRequesting = true;

    this.quoteService.deleteQuoteAreaItem(this.quoteAreaItem.quoteAreaItem_ID)
      .pipe(finalize(() => this.isRequesting = false))
      .subscribe(
        {next: (result) => {
          if (result) {
            this.router.navigate(['/quote/' + this.quote_ID]);
          }
        },
        error: (error: string) => this.errors = error});
  }

  ngOnDestory() {
    this.areaSubscription.unsubscribe();
  }

}
